import React, { useState, useEffect } from 'react';
import axiosInstance from "../../utils/axiosInstance";

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    counts: {
      dueTodayCount: 0,
      overdueCount: 0,
      neverFollowupCount: 0,
      wonDealStageCount: 0,
      lossDealStageCount: 0,
      todayVisitTotal: 0,
      estimateCount: 0,
      todayNewLead: 0
    },
    todayFollowups: {
      total: 0,
      list: []
    },
    todayVisits: {
      total: 0,
      list: []
    },
    leadStageChart: []
  });
  
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);

  // Get business ID from localStorage
  const getBusinessId = () => {
    return localStorage.getItem("businessId") || "";
  };

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
    fetchUsers();
  }, []);

  const fetchDashboardData = async (userId = '') => {
    try {
      setLoading(true);
      const businessId = getBusinessId();
      
      const params = {
        business_id: businessId
      };
      
      if (userId) {
        params.user_id = userId;
      }
      
      const response = await axiosInstance.get('/admin/api/dashboard', { params });
      
      if (response.data.Status === 1) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const businessId = getBusinessId();
      const response = await axiosInstance.get('/api/user', {
        params: { 
          userType: 'Company',
          business: businessId 
        }
      });
      
      if (response.data.users) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleUserFilterChange = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    fetchDashboardData(userId);
  };

  // Calculate pie chart data
  const calculatePieChart = () => {
    if (!dashboardData.leadStageChart || dashboardData.leadStageChart.length === 0) {
      return null;
    }

    const total = dashboardData.leadStageChart.reduce((sum, item) => sum + item.count, 0);
    if (total === 0) return null;

    const colors = ['#FF6B9D', '#4ECDC4', '#95E1D3', '#FFA07A', '#DDA15E', '#BC6C25', '#6C5CE7', '#A8A8A8'];
    
    let currentAngle = -90;
    const segments = dashboardData.leadStageChart.map((item, index) => {
      const percentage = (item.count / total) * 100;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      
      return {
        ...item,
        percentage,
        startAngle,
        endAngle: currentAngle,
        color: colors[index % colors.length]
      };
    });

    return segments;
  };

  const pieSegments = calculatePieChart();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      {/* Page Title */}
      <div className="mb-4">
        <h2 className="fw-bold mb-0">Dashboard</h2>
      </div>

      {/* Stats Cards Row */}
      <div className="row g-3 mb-4">
        {/* Lead Overdue */}
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div>
            <div className="card-bg p-2">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px', backgroundColor: '#FFE5E5' }}>
                  <i className="fa fa-exclamation-triangle" style={{ color: '#FF6B6B', fontSize: '18px' }}></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: '28px', color: '#2c3e50' }}>
                {dashboardData.counts.overdueCount}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Lead Overdue</p>
            </div>
          </div>
        </div>

        {/* Lead Without Follow-up */}
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div>
            <div className="card-bg p-2">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px', backgroundColor: '#FFF4E5' }}>
                  <i className="fa fa-bell" style={{ color: '#FFA726', fontSize: '18px' }}></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: '28px', color: '#2c3e50' }}>
                {dashboardData.counts.neverFollowupCount}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Lead Without Follow-up</p>
            </div>
          </div>
        </div>

        {/* Today New Lead */}
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div>
            <div className="card-bg p-2">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px', backgroundColor: '#E8EAF6' }}>
                  <i className="fa fa-star" style={{ color: '#5C6BC0', fontSize: '18px' }}></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: '28px', color: '#2c3e50' }}>
                {dashboardData.counts.todayNewLead}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Today New Lead</p>
            </div>
          </div>
        </div>

        {/* Lead Win */}
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div>
            <div className="card-bg p-2">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px', backgroundColor: '#E8F5E9' }}>
                  <i className="fa fa-check-circle" style={{ color: '#66BB6A', fontSize: '18px' }}></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: '28px', color: '#2c3e50' }}>
                {dashboardData.counts.wonDealStageCount}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Lead Win</p>
            </div>
          </div>
        </div>

        {/* Lead Loss */}
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div>
            <div className="card-bg p-2">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px', backgroundColor: '#FFEBEE' }}>
                  <i className="fa fa-times-circle" style={{ color: '#EF5350', fontSize: '18px' }}></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: '28px', color: '#2c3e50' }}>
                {dashboardData.counts.lossDealStageCount}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Lead Loss</p>
            </div>
          </div>
        </div>

        {/* Estimate Count */}
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div>
            <div className="card-bg p-2">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle d-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px', backgroundColor: '#E3F2FD' }}>
                  <i className="fa fa-file-text" style={{ color: '#42A5F5', fontSize: '18px' }}></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ fontSize: '28px', color: '#2c3e50' }}>
                {dashboardData.counts.estimateCount}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Estimate Count</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tables and Chart Section */}
      <div className="row g-3">
        {/* Today's Follow-Ups Table */}
        <div className="col-lg-5">
          <div className="card border-1" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-0 p-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>Today's Follow-Ups</h6>
              <span className="badge rounded-pill" style={{ backgroundColor: '#E8EAF6', color: '#5C6BC0', padding: '6px 12px' }}>
                {dashboardData.todayFollowups.total}
              </span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                <table className="table table-hover mb-0">
                  <thead style={{ backgroundColor: '#F5F7FA', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th className="text-muted fw-normal py-3 px-3" style={{ fontSize: '12px', borderBottom: '2px solid #E8EAF6' }}>Sr. No.</th>
                      <th className="text-muted fw-normal py-3 px-3" style={{ fontSize: '12px', borderBottom: '2px solid #E8EAF6' }}>Lead</th>
                      <th className="text-muted fw-normal py-3 px-3" style={{ fontSize: '12px', borderBottom: '2px solid #E8EAF6' }}>Mobile Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.todayFollowups.list.length > 0 ? (
                      dashboardData.todayFollowups.list.map((followup, index) => (
                        <tr key={followup._id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td className="py-3 px-3" style={{ fontSize: '13px', color: '#5f6368' }}>{index + 1}</td>
                          <td className="py-3 px-3" style={{ fontSize: '13px', color: '#2c3e50' }}>
                            {followup.lead?.name || 'N/A'}
                          </td>
                          <td className="py-3 px-3" style={{ fontSize: '13px', color: '#5f6368' }}>
                            {followup.lead?.mobile || 'N/A'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-4" style={{ color: '#9e9e9e', fontSize: '13px' }}>
                          No Follow ups today.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Lead Stage Chart */}
        <div className="col-lg-3">
          <div className="card border-1" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-0 p-3">
              <h6 className="mb-2 fw-bold" style={{ color: '#2c3e50' }}>Lead Stage</h6>
              <div className="mt-2">
                <label className="text-muted mb-1" style={{ fontSize: '11px' }}>Select User</label>
                <select 
                  className="form-select form-select-sm" 
                  value={selectedUser}
                  onChange={handleUserFilterChange}
                  style={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                >
                  <option value="">All User</option>
                  {users.map((user) => (
                    <option key={user._id || user.id} value={user._id || user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="card-body p-3">
              {pieSegments && pieSegments.length > 0 ? (
                <div>
                  <div className="d-flex justify-content-center mb-3">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      {pieSegments.map((segment, index) => {
                        const radius = 80;
                        const centerX = 100;
                        const centerY = 100;
                        
                        const startAngleRad = (segment.startAngle * Math.PI) / 180;
                        const endAngleRad = (segment.endAngle * Math.PI) / 180;
                        
                        const x1 = centerX + radius * Math.cos(startAngleRad);
                        const y1 = centerY + radius * Math.sin(startAngleRad);
                        const x2 = centerX + radius * Math.cos(endAngleRad);
                        const y2 = centerY + radius * Math.sin(endAngleRad);
                        
                        const largeArcFlag = segment.percentage > 50 ? 1 : 0;
                        
                        return (
                          <path
                            key={index}
                            d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                            fill={segment.color}
                            stroke="#fff"
                            strokeWidth="2"
                          />
                        );
                      })}
                    </svg>
                  </div>

                  <div className="legend">
                    {pieSegments.map((segment, index) => (
                      <div key={index} className="d-flex align-items-center mb-2">
                        <div 
                          className="rounded me-2" 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: segment.color,
                            flexShrink: 0
                          }}
                        ></div>
                        <span style={{ fontSize: '12px', color: '#5f6368' }}>
                          {segment.stage}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted mb-0" style={{ fontSize: '13px' }}>No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Today's Lead Visit Table */}
        <div className="col-lg-4">
          <div className="card border-1" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-white border-0 p-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>Today's Lead Visit</h6>
              <span className="badge rounded-pill" style={{ backgroundColor: '#E8EAF6', color: '#5C6BC0', padding: '6px 12px' }}>
                {dashboardData.todayVisits.total}
              </span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                <table className="table table-hover mb-0">
                  <thead style={{ backgroundColor: '#F5F7FA', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th className="text-muted fw-normal py-3 px-3" style={{ fontSize: '12px', borderBottom: '2px solid #E8EAF6' }}>Sr. No.</th>
                      <th className="text-muted fw-normal py-3 px-3" style={{ fontSize: '12px', borderBottom: '2px solid #E8EAF6' }}>Lead</th>
                      <th className="text-muted fw-normal py-3 px-3" style={{ fontSize: '12px', borderBottom: '2px solid #E8EAF6' }}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.todayVisits.list.length > 0 ? (
                      dashboardData.todayVisits.list.map((visit, index) => (
                        <tr key={visit._id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td className="py-3 px-3" style={{ fontSize: '13px', color: '#5f6368' }}>{index + 1}</td>
                          <td className="py-3 px-3" style={{ fontSize: '13px', color: '#2c3e50' }}>
                            {visit.lead?.name || visit.name || 'N/A'}
                          </td>
                          <td className="py-3 px-3" style={{ fontSize: '13px', color: '#5f6368' }}>
                            {visit.note ? (visit.note.length > 30 ? visit.note.substring(0, 30) + '...' : visit.note) : '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-4" style={{ color: '#9e9e9e', fontSize: '13px' }}>
                          No visits today.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;